import type { MediaItem } from "@/mocks/media";
import { formatRating, typeLabel } from "@/utils/format";

interface DetailHeroProps {
  item: MediaItem;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onShare: () => void;
  onDemo: (action: string) => void;
}

export default function DetailHero({
  item,
  isFavorite,
  onToggleFavorite,
  onShare,
  onDemo,
}: DetailHeroProps) {
  return (
    <section className="relative w-full">
      <div className="absolute inset-0 h-[280px] md:h-[360px] overflow-hidden">
        <img
          src={item.backdrop || item.poster}
          alt={`${item.title} 剧照`}
          title={`${item.title} - ${typeLabel(item.type)}`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/80 to-background-50/40"></div>
      </div>

      <div className="relative w-full px-4 md:px-8 pt-10 md:pt-16 pb-8">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="w-40 sm:w-48 md:w-56 shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-300 bg-background-200">
              <img
                src={item.poster}
                alt={`${item.title} 海报`}
                title={`${item.title} 在线查询`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-500 text-background-50">
                {typeLabel(item.type)}
              </span>
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-900 border border-secondary-200">
                {item.region}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
              {item.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600 mb-4">
              <span className="flex items-center gap-1 text-primary-400 font-semibold">
                <i className="ri-star-fill"></i>
                {formatRating(item.rating)}
              </span>
              <span>{item.year} 年</span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {item.duration}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-price-tag-3-line"></i>
                {item.genres.join(" / ")}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-6">
              <button
                type="button"
                onClick={() => onDemo("播放")}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                立即播放
              </button>
              <button
                type="button"
                onClick={() => onDemo("下载")}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 bg-background-100 text-foreground-800 text-sm font-medium hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line"></i>
                下载
              </button>
              <button
                type="button"
                onClick={onToggleFavorite}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                  isFavorite
                    ? "bg-accent-500 border-accent-500 text-background-50"
                    : "bg-background-100 border-background-300 text-foreground-800 hover:border-accent-400"
                }`}
              >
                <i className={isFavorite ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                {isFavorite ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={onShare}
                className="w-10 h-10 flex items-center justify-center rounded-md border border-background-300 bg-background-100 text-foreground-800 hover:border-primary-400 transition-colors cursor-pointer"
                aria-label="分享"
              >
                <i className="ri-share-forward-line"></i>
              </button>
            </div>

            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {item.description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}